import { configService } from './ConfigService'
import { AuthTokenService } from './AuthTokenService'
import { webViewParamsService } from './WebViewParamsService'
import { ApolloGraphQLClient } from '@/infrastructure/graphql/apolloClient'
import { AxiosHttpClient } from '@/infrastructure/http/axiosClient'
import { container } from '@/core/container'
import { TYPES } from '@/core/container/types'
import { setupContainer } from '@/core/container/setup'
import { IAuthRepository } from '@/core/domain/repositories/IAuthRepository'
import { AuthSession, User } from '@/core/domain/entities/User'
import { isOk } from '@/shared/utils/Result'

export class AppBootstrap {
  private static instance: AppBootstrap
  private isInitialized = false

  private constructor() {}

  static getInstance(): AppBootstrap {
    if (!AppBootstrap.instance) {
      AppBootstrap.instance = new AppBootstrap()
    }
    return AppBootstrap.instance
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return
    }

    try {
      console.log('🚀 Initializing Garupa Application...')
      
      // Initialize configuration
      const config = configService.getConfig()
      console.log(`📝 Environment: ${config.app.environment}`)
      console.log(`🔗 GraphQL URI: ${config.graphql.uri}`)
      console.log(`🔗 API Base URL: ${config.api.baseUrl}`)

      // Initialize token service
      const tokenService = AuthTokenService.getInstance()
      
      // Create HTTP clients with token integration
      const apolloClient = new ApolloGraphQLClient(
        config.graphql,
        () => tokenService.getToken()
      )

      const axiosClient = new AxiosHttpClient(
        {
          baseUrl: config.api.baseUrl,
          graphqlEndpoint: '/graphql',
          timeout: config.api.timeout,
          retries: 3,
          retryDelay: 1000,
        },
        () => tokenService.getToken()
      )

      // Register clients in container first
      container.registerInstance(TYPES.GraphQLClient, apolloClient)
      container.registerInstance(TYPES.AxiosClient, axiosClient)
      
      // Setup DI container (will skip already registered services)
      setupContainer()
      console.log('✅ DI Container setup complete')

      // Handle webview authentication
      await this.handleWebViewAuthentication(tokenService)

      this.isInitialized = true
      console.log('✅ Application initialized successfully')

    } catch (error) {
      console.error('❌ Failed to initialize application:', error)
      throw error
    }
  }

  private async handleWebViewAuthentication(tokenService: AuthTokenService): Promise<void> {
    const apiToken = webViewParamsService.getApiToken()

    if (!apiToken) {
      console.log('🔍 No webview token found, continuing with normal auth flow')
      return
    }

    console.log('🔍 WebView token found, validating...')

    tokenService.setWebViewToken(apiToken)

    let result
    try {
      const authRepository = container.resolve<IAuthRepository>(TYPES.AuthRepository)
      result = await authRepository.checkAuth(apiToken)
    } catch (error) {
      console.error('❌ Auth repository not available during bootstrap:', error)
      return
    }

    if (!isOk(result)) {
      console.error('❌ WebView token validation failed:', result.error.message)
      tokenService.clearWebViewToken()
      return // Don't throw error, let user authenticate normally
    }

    const user = result.value as User

    console.log('✅ WebView authentication successful', {
      userId: user.id,
      userType: user.type
    })

    const authRepository = container.resolve<IAuthRepository>(TYPES.AuthRepository)
    const session: AuthSession = {
      user,
      accessToken: apiToken,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
    }
    await authRepository.storeSession(session)

    console.log('💾 Session stored for AuthProvider')
  }



  getInitializationStatus(): boolean {
    return this.isInitialized
  }

  async shutdown(): Promise<void> {
    console.log('🛑 Shutting down application...')
    
    // Clear caches
    try {
      const apolloClient = container.resolve<ApolloGraphQLClient>(TYPES.GraphQLClient)
      apolloClient.clearCache()
    } catch {
      // Client not registered or already cleared
    }

    this.isInitialized = false
    console.log('✅ Application shutdown complete')
  }
}

export const appBootstrap = AppBootstrap.getInstance()